async function urlBase64ToUint8Array(base64String) {

  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}


async function registerPush(vapidPublicKey) {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
    console.warn("Push API not supported in this browser.");
    return null;
  }
  try {
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (sub)
      return sub;

    // Normalize VAPID public key: accept object shapes, PEM-wrapped keys, or base64/url-safe strings
    try {
      if (vapidPublicKey && typeof vapidPublicKey === 'object') {
        // sometimes server might return an object { publicKey: '...' }
        if (vapidPublicKey.publicKey) vapidPublicKey = vapidPublicKey.publicKey;
        else if (vapidPublicKey.publicKeyBase64) vapidPublicKey = vapidPublicKey.publicKeyBase64;
        else vapidPublicKey = String(vapidPublicKey);
      }
    } catch (e) { /* ignore */ }

    if (typeof vapidPublicKey === 'string') {
      vapidPublicKey = vapidPublicKey.trim();
      // PEM format? extract base64 section
      if (vapidPublicKey.indexOf('-----BEGIN') !== -1) {
        const match = vapidPublicKey.match(/-----BEGIN[^-]+-----(?:\r\n|\n)([\s\S]+?)(?:\r\n|\n)-----END[^-]+-----/);
        if (match && match[1]) {
          vapidPublicKey = match[1].replace(/\s+/g, '');
          console.debug('[notify] extracted base64 from PEM VAPID public key');
        }
      }
    }

    console.debug('[notify] VAPID public key length (chars):', vapidPublicKey ? vapidPublicKey.length : 0);
    const convertedKey = await urlBase64ToUint8Array(vapidPublicKey);
    try {
      console.debug('[notify] subscribing push; key length=', convertedKey && convertedKey.length, 'type=', Object.prototype.toString.call(convertedKey));
      sub = await reg.pushManager.subscribe({userVisibleOnly: true, applicationServerKey: convertedKey });
    }
    catch (innerErr) {
      // Some browsers/proxies may accept an ArrayBuffer instead of Uint8Array — try that as a fallback
      try {
        if (innerErr && innerErr.name === 'AbortError') console.warn('Push subscription aborted on first attempt, trying ArrayBuffer fallback');
        const buf = convertedKey && convertedKey.buffer ? convertedKey.buffer : convertedKey;
        sub = await reg.pushManager.subscribe({userVisibleOnly: true, applicationServerKey: buf });
      }
      catch (finalErr) {
        throw finalErr;
      }
    }
    return sub;
  }
  catch (e) {
    if (e.name === "AbortError") {
      console.error("Push subscription aborted. Likely invalid VAPID key or browser push service error.");
    }
    else {
      console.error("Push registration failed:", e);
    }
    return null;
  }
}

async function sendSubscriptionToServer(subscription) {
  try {
    const payload = subscription.toJSON ? subscription.toJSON() : subscription;

    const hub = (typeof window !== 'undefined' && window.notificationHub) ? window.notificationHub : null;

    if (hub) {
      try {
        // hub.invoke expects the SignalR connection wrapper
        await hub.invoke('SubscribePush', payload);
        return;
      }
      catch (e) {
        console.warn('SubscribePush via hub failed', e);
      }
    }
    try {
      await fetch('/api/push/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
    } catch (e) {
      console.warn('HTTP push subscribe fallback failed', e);
    }
  } catch (e) {
    console.warn('sendSubscriptionToServer failed', e);
  }
}

async function tryPushSubscribe() {
  try {
    if (!('serviceWorker' in navigator) || !window.isSecureContext) {
      console.warn("Push requires HTTPS and a registered service worker.");
      return;
    }
    // ensure service worker registered
    try {
      if (!navigator.serviceWorker.controller) {
        await navigator.serviceWorker.register('/sw.js');
        console.log('Service worker registered for push');
      }
    } catch (e) {
      console.warn('Service worker registration failed', e);
    }

    if (Notification.permission === 'default') {
      // Permission not decided yet. The global consent UI in layout should prompt the user.
      // Avoid showing any additional toasts here to prevent duplicates.
      return;
    }
    else if (Notification.permission !== 'granted') {
      return;
    }

    let key = null;
    const hub = (typeof window !== 'undefined' && window.notificationHub) ? window.notificationHub : null;
    if (hub && typeof hub.invoke === 'function') {
      try {
        key = await hub.invoke('GetVapidPublic');
      }
      catch (e) {
        console.warn('GetVapidPublic via hub failed', e);
      }
    }
    if (!key) {
      try {
        const r = await fetch('/api/push/vapidpublic');
        if (r.ok) {
          const json = await r.json();
          key = json.publicKey || json.publicKeyBase64 || json.public;
        }
      } catch (e) {
        console.warn("Failed to fetch VAPID public key", e);
      }
    }
    // If no VAPID key configured on the server, try a dev-only generator when running on localhost
    if (!key) {
      try {
        // Try development-only endpoint when server has no VAPID key configured.
        // This endpoint is mapped only in Development and will 404 in production — that's fine.
        const r2 = await fetch('/dev/vapid/generate');
        if (r2.ok) {
          const j2 = await r2.json();
          key = j2.publicKey || j2.public;
          console.info('Using development-generated VAPID public key for push subscription');
        }
      } catch (e) {
        console.warn('Failed to fetch dev VAPID key', e);
      }
    }

    if (!key)
      return;

    const sub = await registerPush(key);
    if (!sub)
      return;

    await sendSubscriptionToServer(sub);
  }
  catch (e) {
    console.warn('Push subscribe flow failed', e);
  }
}

if (document.readyState === 'loading')
  document.addEventListener('DOMContentLoaded', tryPushSubscribe);
else
  tryPushSubscribe();

// Expose a function to start notifications from UI (Cv.cshtml calls this)
window.startNotifications = async function() {
  try {
    if (!('Notification' in window)) {
      alert('Notifications are not supported in this browser.');
      return;
    }
    if (Notification.permission === 'default') {
      const p = await Notification.requestPermission();
      if (p !== 'granted') return;
    }
    if (Notification.permission !== 'granted') return;
    await tryPushSubscribe();
  } catch (e) {
    console.warn('startNotifications failed', e);
  }
};

// Global consent UI is managed centrally in site.js. Removed local duplicate wiring to avoid conflicts.
